import useCreateCustomerBulk from "@/services/customer/hooks/useCreateCusromerBulk";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  Group,
  InputBase,
  List,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { randomId, useDisclosure, useListState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconBookUpload,
  IconCheck,
  IconSearch,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { cleanContacts, parseVcfContacts } from "./helper";
import { Contact, CreateCustomersResponse } from "./type";
import Header from "@/components/header";

function ImportContact() {
  const [contacts, handlers] = useListState<Contact>();
  const [search, setSearch] = useState<string>("");
  const [opened, { open, close }] = useDisclosure(false);
  const [response, setResponse] = useState<CreateCustomersResponse>();
  const { mutate, isLoading } = useCreateCustomerBulk();

  const handleContacts = useCallback(
    (contactsData: unknown) => {
      const cleanData = cleanContacts(contactsData as Contact[]);
      handlers.setState(cleanData?.map((x) => ({ ...x, key: randomId() })));
    },
    [handlers]
  );

  const allChecked = contacts.every((value) => value.checked);
  const indeterminate = contacts.some((value) => value.checked) && !allChecked;

  useEffect(() => {
    window.handleContacts = handleContacts;
  }, [handleContacts]);

  const filteredContacts = contacts?.filter((contact) => {
    const normalizedSearch = search.toLowerCase().trim();
    return (
      contact?.firstName?.toLowerCase().includes(normalizedSearch) ||
      contact?.lastName?.toLowerCase().includes(normalizedSearch) ||
      contact?.phoneNumber?.includes(normalizedSearch)
    );
  });

  const sendToServer = () => {
    const selectedContacts = contacts?.filter((x) => x.checked);
    if (selectedContacts.length === 0) {
      notifications.show({
        message: "لطفا مخاطبین خود را انتخاب نمایید.",
        color: "yellow",
      });
      return;
    }
    mutate(
      {
        items: selectedContacts?.map((x) => ({
          firstName: x.firstName,
          lastName: x.lastName,
          phoneNumber: x.phoneNumber,
        })),
      },
      {
        onSuccess(data) {
          open();
          setResponse(data);
        },
        onError() {
          notifications.show({
            message: "خطا لطفا دوباره تلاش فرمایید",
            color: "red",
          });
        },
      }
    );
  };

  const checkItems = (item: Contact, checked: boolean) => {
    handlers.setState((current) =>
      current.map((value) => {
        if (value?.key === item?.key) return { ...value, checked };
        return value;
      })
    );
  };

  const count = contacts.filter((x) => x.checked).length;
  // Preview drawer is currently not in use; keep minimal state

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();

    const parsedContacts = parseVcfContacts(text);

    handlers?.setState(parsedContacts?.map((x) => ({ ...x, key: randomId() })));
  };

  // moved vcf parsing to helper (parseVcfContacts)

  // ارسال به API (اختیاری)
  // const uploadToServer = async () => {
  //   try {
  //     const res = await fetch("/api/contacts/upload", {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify(contacts),
  //     });

  //     const data = await res.json();
  //     console.log("Server Response:", data);
  //     alert("Contacts uploaded!");
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  return (
    <>
      <Container size="md" px="0" w="100%">
        <Header showBack title="دریافت مخاطبین از تلفن همراه" />
      </Container>

      <Container size="md" w="100%">
        <Stack h="calc(100svh - 120px)">
          <Stack
            styles={{
              root: {
                flexGrow: 1,
              },
            }}
            px="md"
            gap="5"
          >
            <Title order={4}>دریافت مخاطبین از تلفن همراه</Title>

            {contacts.length > 0 ? (
              <>
                <Group justify="space-between">
                  <Checkbox
                    checked={allChecked}
                    py="md"
                    indeterminate={indeterminate}
                    label={`انتخاب همه`}
                    onChange={() =>
                      handlers.setState((current) =>
                        current.map((value) => ({
                          ...value,
                          checked: !allChecked,
                        }))
                      )
                    }
                  />
                  <Group>
                    {count > 0 ? (
                      <Badge color="green">انتخاب شده : {count}</Badge>
                    ) : null}
                    <Badge color="dark">تعداد کل : {contacts.length}</Badge>
                  </Group>
                </Group>
                <Stack mb="xs">
                  <InputBase
                    leftSection={<IconSearch size={15} />}
                    size="xs"
                    value={search}
                    placeholder="جستجو..."
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Stack>
                <ScrollArea h="calc(100svh - 330px)">
                  {filteredContacts?.map((x) => (
                    <Card withBorder key={x?.key} p="xs" mb="xs">
                      <Group justify="space-between" align="center">
                        <Checkbox
                          label={x?.displayName}
                          key={x?.key}
                          checked={x?.checked}
                          onChange={(event) =>
                            checkItems(x, event.currentTarget.checked)
                          }
                        />
                        <Text size="xs" dir="ltr">
                          {x?.phoneNumber}
                        </Text>
                      </Group>
                    </Card>
                  ))}
                </ScrollArea>
              </>
            ) : (
              <>
                <Stack pos="relative" mt="md">
                  <input
                    type="file"
                    accept=".vcf"
                    onChange={handleFileChange}
                    style={{
                      position: "absolute",

                      left: 0,
                      width: "100%",
                      height: "150px",
                      background: "red",
                      opacity: 0,
                    }}
                  />
                  <Stack
                    styles={{
                      root: {
                        height: "150px",
                        borderRadius: "var(--mantine-radius-md)",
                        border: "2px dashed var(--mantine-color-gray-3)",
                      },
                    }}
                    justify="center"
                    align="center"
                    gap="xs"
                    p="lg"
                  >
                    <IconBookUpload
                      size={50}
                      color="var(--mantine-color-gray-2)"
                    />
                    <Text size="sm" ta="center" c="var(--mantine-color-gray-7)">
                      فایل مربوط به دفترچه مخاطبین تلفن همراه با فرمت .vcf را
                      انتخاب کنید.
                    </Text>
                  </Stack>

                  {/* <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon icon-tabler icon-tabler-book-download"
                width="100"
                height="100"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="var(--mantine-color-gray-2)"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M12 20h-6a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12v5" />
                <path d="M13 16h-7a2 2 0 0 0 -2 2" />
                <path d="M15 19l3 3l3 -3" />
                <path d="M18 22v-9" />
              </svg> */}
                  {/* <Alert>
                <Text ta="center" size="xs">
                  برای دریافت لیست مشتریان خوداز دفترچه مخاطبین تلفن همراه و
                  اضافه کردن به لیست مشتریان لطفا بر روی دریافت لیست کلیک
                  نمایید.
                </Text>
              </Alert> */}
                </Stack>
                <Stack>
                  <Card bg="blue.1">
                    <Text size="xs" fw={500} pb="xs">
                      خروجی گرفتن (Export) از لیست مخاطبین در گوشی همراه{" "}
                    </Text>
                    <List size="xs" type="ordered">
                      <List.Item>
                        <Text size="xs">
                          اپلیکیشن Contacts (یا People) را روی گوشی باز کنید.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text size="xs">
                          به منو بروید — معمولاً با آیکن سه‌نقطه یا سه‌خط در بالا
                          سمت راست یا با دکمه‌ی سخت‌افزاری منو.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text size="xs">
                          گزینه‌ی Settings یا مستقیماً Export را انتخاب کنید. (اگر
                          اول Settings را زدید، سپس دنبال Export بگردید.)
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text size="xs">
                          وقتی گزینه‌ی Export را انتخاب کردید، از شما پرسیده
                          می‌شود که می‌خواهید مخاطبین را کجا ذخیره کنید — Export
                          to .vcf file یا Save to device را انتخاب کنید.
                        </Text>
                      </List.Item>

                      <List.Item>
                        <Text size="xs">
                          مسیر ذخیره‌سازی را تأیید کنید (مثلاً پوشه‌ی Downloads یا
                          Contacts). سپس OK یا Save را بزنید.
                        </Text>
                      </List.Item>

                      <List.Item>
                        <Text size="xs">
                          حالا فایل با فرمت .vcf ساخته شده و داخل حافظه‌ی گوشی
                          ذخیره شده است.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text size="xs">
                          برای پیدا کردن فایل به اپ File Manager یا Files بروید،
                          پوشه‌ای که انتخاب کردید (مثلاً Downloads) را باز کنید و
                          فایل .vcf را می‌بینید.
                        </Text>
                      </List.Item>
                    </List>
                  </Card>
                </Stack>
              </>
            )}
          </Stack>

          {contacts.length > 0 ? (
            <Stack px="md" pb="md">
              <Button size="md" onClick={sendToServer} loading={isLoading}>
                ارسال
              </Button>
            </Stack>
          ) : (
            <></>
          )}
        </Stack>
      </Container>
      <Modal centered opened={opened} onClose={close} title="عملیات موفق">
        <Card withBorder p="0">
          <Group justify="space-between" p="sm">
            <Group align="center" gap="xs">
              <ActionIcon color="green" variant="light" size="xs">
                <IconCheck />
              </ActionIcon>
              <Text size="xs">مخاطبین ثبت شده</Text>
            </Group>
            <Text size="xs">{response?.registeredCount}</Text>
          </Group>
          <Divider />
          <Group justify="space-between" p="sm">
            <Group align="center" gap="xs">
              <ActionIcon color="yellow" variant="light" size="xs">
                <IconAlertCircle />
              </ActionIcon>
              <Text size="xs">مخاطبین موجود </Text>
            </Group>
            <Text size="xs">{response?.existCount}</Text>
          </Group>
        </Card>
        <Button mt="md" fullWidth onClick={close}>
          متوجه شدم
        </Button>
      </Modal>

      {/* <CustomDrawer
        opened={preview}
        onClose={close}
        close={close}
        position="bottom"
        radius="md"
        title="توجه"
        custom="help"
        h="450px"
      >
        <Stack justify="space-between">
          <Stack justify="center" align="center">
            <Alert>
              <Text size="xs" ta="center">
                کاربر گرامی در صورتی که مشتریان خود را در فایل اکسل و یا دیتابیس
                خاصی وارد نموده ایید ، میتوانید با تماس با واحد پشتیبانی نسبت به
                وارد کردن اطلاعات مشتریان خود به صورت اتومات اقدام نمایید.
              </Text>

              <Anchor href="tel::09362527637">
                <Group justify="center" align="center" gap="xs" mt="md">
                  <IconPhone size={15} />
                  <Text size="xs" ta="center">
                    تماس با پشتیبانی
                  </Text>
                </Group>
              </Anchor>
            </Alert>
            <Image src={ContactList} w={200} />
          </Stack>
          <Button onClick={closePreview} size="md">
            ادامه
          </Button>
        </Stack>
      </CustomDrawer> */}
    </>
  );
}

export default ImportContact;
